import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink, Plus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useDiscardGuard } from '../hooks/useDiscardGuard';

import { CommandBlock } from './CommandBlock';
import { CreateLensDialog } from './CreateLensDialog';
import { CredentialField } from './CredentialField';
import { Field, FieldGroup } from './Field';
import { StepIndicator, StepDescriptor } from './StepIndicator';
import { TagPicker, PickableTag } from './TagPicker';
import { CheckboxList, checkboxListProps } from './CheckboxList';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { FieldValue } from './FieldValue';
import { Label } from './ui/label';
import { apiService } from '../services/api';
import { Lens, Tag, TemplateProcessor, TemplateProcessorVersion } from '../types/api';
import { useInfiniteList } from '../hooks/useInfiniteList';
import { useResource } from '../hooks/useResource';
import { batchFetcher } from '../lib/listPage';
import { hasListError } from './DataTable';
import { StatusBadge } from './StatusBadge';
import { formatRelativeTime } from '../lib/formatters';
import { classifyError, environmentRefusal, humanizeError } from '../lib/errors';
import { ErrorState } from './ErrorState';
import { FormError } from './FormError';
import { previewSlug } from '../lib/slug';
import { SENSOR_TYPE_KEYS, SENSOR_TYPE_LABELS } from '../lib/labels';
import { permissionLabel } from '../lib/permissions';
import { EnvironmentVariablesField } from './EnvironmentVariablesField';
import {
  environmentRowsFrom,
  environmentSourceFor,
  hasEnvironmentErrors,
  reconcileEnvironmentErrors,
  sameEnvironment,
  toEnvironmentPayload,
  validateEnvironmentRows,
  type EnvironmentErrors,
  type EnvironmentRow,
} from '../lib/environmentForm';

/** The lens requirement, as help before a submit is tried and as the group's error after. */
const LENS_REQUIREMENT = 'Pick at least one lens. Without it the version can be approved and still reach nobody.';

/**
 * SDK version the scaffold pins.
 *
 * PyPI's latest is 0.0.6; the SDK repo is at 0.0.7 but has never been
 * released, and publishing is manual. Pinning is the only defence against that
 * drift — an unpinned install silently tracks whatever PyPI last accepted.
 */
const SDK_VERSION = '0.0.6';

/** Where the publishing CLI is served from. See the flow's step 3. */
const CLI_DOWNLOAD_URL = 'https://portal.streetscapes.ai/downloads/dory_util.py';

const GUIDE_URL = 'https://wiki.streetscapes.ai/guides/02-template-developer-guide/';

const SENSOR_TYPES = SENSOR_TYPE_KEYS;

/**
 * What the wizard is being opened to do.
 *
 * `setup` is the full eight-step onboarding: no template exists yet, or one
 * exists but has never shipped an image. `new-version` is shipping version N+1
 * of a template that already works — a strictly smaller flow, because most of
 * setup is either impossible or destructive on a second run.
 */
export type PublishWizardMode = 'setup' | 'new-version';

const SETUP_STEPS: StepDescriptor[] = [
  { id: 'create', label: 'Create template' },
  { id: 'credentials', label: 'Get publisher credential' },
  { id: 'install', label: 'Install libraries' },
  { id: 'scaffold', label: 'Initial setup' },
  { id: 'build', label: 'Build version container' },
  { id: 'push', label: 'Upload version container' },
  { id: 'submit', label: 'Submit for review' },
  { id: 'summary', label: 'Summary' },
];

/**
 * Shipping the next version. Three of the eight setup steps are deliberately
 * absent, and each omission is load-bearing:
 *
 *  - `create`   — impossible. With a non-null template the create step renders
 *                 a read-only confirmation, never the form, and POST
 *                 /template-processors would 409 on the existing slug.
 *  - `install`  — one-time per developer MACHINE (venv, pip install, curl of
 *                 the CLI). It makes no API call and nothing about a second
 *                 version invalidates it.
 *  - `scaffold` — actively DESTRUCTIVE on a re-run: it regenerates the whole
 *                 project over the edited `main.py`, and that edited `main.py`
 *                 IS version 2. Offering it here is offering to delete the work
 *                 the user opened this flow to publish.
 *
 * Lens attachment is NOT one of the omissions any more. It is per-TEMPLATE
 * rather than per-version, so a second version arrives with it already
 * satisfied — but it is part of what an administrator reviews, so the Submit
 * for Review step shows it in both modes rather than asking a reviewer to take
 * the routing on trust.
 */
const NEW_VERSION_STEPS: StepDescriptor[] = [
  { id: 'credentials', label: 'Get publisher credential' },
  { id: 'build', label: 'Build version container' },
  { id: 'push', label: 'Upload version container' },
  { id: 'submit', label: 'Submit for review' },
  { id: 'summary', label: 'Summary' },
];

/** Steps whose work happens in a terminal — dismissing mid-way is guarded. */
const TERMINAL_STEP_IDS = new Set(['install', 'scaffold', 'build', 'push']);

/** Steps with no server-side truth; the user acknowledges them. */
const ACK_STEP_IDS = ['install', 'scaffold', 'build', 'push'] as const;
type AckStepId = (typeof ACK_STEP_IDS)[number];

/**
 * What stands in for the publisher secret on screen.
 *
 * Same character and width as CredentialField's mask, so the two steps agree
 * about what a hidden secret looks like.
 */
const SECRET_MASK = '\u2022'.repeat(24);

/** A stable empty list while no versions have been read. */
const NO_VERSIONS: TemplateProcessorVersion[] = [];

/** The API's own tag format check, applied before we spend a round trip. */
const isValidImageTag = (tag: string): boolean => /^[A-Za-z0-9._-]{1,64}$/.test(tag);

interface PublishTemplateWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Existing template to resume; null starts at creation. */
  template: TemplateProcessor | null;
  organizationId: string;
  accessToken: string;
  /** Fired after any mutation so the page can refresh its list. */
  onTemplateChanged: (template: TemplateProcessor) => void;
  /** Whether the caller may read publisher credentials. */
  mayReadCredentials: boolean;
  /**
   * Whether the caller may change which lenses this template belongs to.
   *
   * Submitting sends the lens selection with the version, and the API refuses a
   * real routing change without `lenses:write`. Without this the picker was
   * offered to everyone and the refusal arrived as a 403 at submit — after the
   * user had ticked boxes and pressed the button.
   */
  mayWriteLenses?: boolean;
  /** Whether the caller may create a lens as public. */
  mayMakeLensPublic?: boolean;
  /**
   * Which flow to run. Optional and defaulting to `setup` so every existing
   * caller keeps its current behaviour.
   */
  mode?: PublishWizardMode;
}

export const PublishTemplateWizard: React.FC<PublishTemplateWizardProps> = ({
  open,
  onOpenChange,
  template,
  organizationId,
  accessToken,
  onTemplateChanged,
  mayReadCredentials,
  // Default true so existing callers keep today's behaviour; MyProcessorTemplates
  // passes the real answer.
  mayWriteLenses = true,
  mayMakeLensPublic = false,
  mode = 'setup',
}) => {
  const steps = mode === 'new-version' ? NEW_VERSION_STEPS : SETUP_STEPS;
  const [stepIndex, setStepIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  /** A taken name, shown under the Name field rather than above the footer. */
  const [createNameError, setCreateNameError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /*
   * The three secondary loads, each with its own failure. A failed load is
   * never an empty list: the empty copy ("No lenses to attach to", the picker's
   * "No event types…") renders only after a load succeeded.
   */
  const [lensesError, setLensesError] = useState<unknown>(null);

  // Step 1
  const [createForm, setCreateForm] = useState({ name: '', description: '' });

  // Step 2
  const [credentials, setCredentials] = useState<{ clientId: string; clientSecret: string } | null>(null);

  // Local acknowledgements — never written to the server.
  const [acks, setAcks] = useState<Record<AckStepId, boolean>>({
    install: false,
    scaffold: false,
    build: false,
    push: false,
  });

  // Submit for Review
  const [imageTag, setImageTag] = useState('v1.0.0');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [selectedSensorTypes, setSelectedSensorTypes] = useState<string[]>(['camera']);
  const [environmentRows, setEnvironmentRows] = useState<EnvironmentRow[]>([]);
  const [environmentErrors, setEnvironmentErrors] = useState<EnvironmentErrors>({ rows: {} });
  /**
   * The version the environment rows were loaded from. Sent with the submission
   * so the API reads a kept secret from THAT version: the new-version prefill is
   * the live version, which is not always the latest.
   */
  const [environmentFromVersionId, setEnvironmentFromVersionId] = useState<string | null>(null);
  // Whether the user edited the environment list since the wizard opened. A
  // list neither loaded from a version nor edited is not sent: the API then
  // inherits the latest version's variables, which this page's template can be
  // too stale to show (a version registered from CI after the page loaded).
  const [environmentTouched, setEnvironmentTouched] = useState(false);
  /**
   * The tag this session actually published, or null.
   *
   * In `new-version` mode it is the only honest proof the Submit for Review
   * step is done. Every other completion rule reads server state that is TEMPLATE
   * scoped and was already true when the dialog opened.
   */
  const [publishedTag, setPublishedTag] = useState<string | null>(null);

  // Submit for Review — lens attachment, part of what a reviewer sees.
  const [lenses, setLenses] = useState<Lens[]>([]);
  const [isLensesLoading, setIsLensesLoading] = useState(false);
  /**
   * The lenses the user has ticked. LOCAL until Submit for Review.
   *
   * Ticking a box used to fire PUT /lenses/{id} immediately, so abandoning the
   * wizard left the template re-routed with nothing submitted, and a failed
   * submit left the routing already changed. It also sent the lens's WHOLE
   * template set from client state, which silently dropped a template a
   * teammate had added since the page loaded.
   */
  const [selectedLensIds, setSelectedLensIds] = useState<string[]>([]);
  /** False until the first lens read seeds the selection for this opening. */
  const [lensSelectionSeeded, setLensSelectionSeeded] = useState(false);
  /**
   * What the Submit step's inputs held when this opening seeded them. The
   * discard guard measures against these, never against the first render: the
   * lens selection arrives asynchronously, and a baseline taken before it
   * would make every wizard read as dirty on a plain close.
   */
  const submitBaselineRef = useRef<{
    imageTag: string;
    tagIds: string[];
    sensorTypes: string[];
    environment: EnvironmentRow[];
  } | null>(null);
  const lensBaselineRef = useRef<string[] | null>(null);
  /** The lens precondition is help until a submit is tried, then an error. */
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isCreateLensOpen, setIsCreateLensOpen] = useState(false);

  // Summary
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<string | null>(null);

  const activeTemplate = template;
  const slug = activeTemplate?.slug ?? 'my-template';

  /**
   * Variables the next version reads from a Kubernetes Secret. The API carries
   * them over from the latest version whatever is submitted, so the field names
   * them rather than offering an edit that would not happen.
   */
  const kubernetesSecretNames = useMemo(
    () =>
      (activeTemplate?.latestVersion?.environment ?? [])
        .filter((variable) => variable.source === 'kubernetes_secret')
        .map((variable) => variable.name),
    [activeTemplate],
  );

  const wasOpenRef = useRef(false);
  /** True until the first load after opening has placed the cursor. */
  const [needsResume, setNeedsResume] = useState(false);

  /**
   * Reset only on the false→true open transition.
   *
   * Keying this on `activeTemplate?.id` instead would fire the moment step 1
   * creates the template — wiping the wizard's progress and the credential
   * step 2 is about to show. (The publisher secret is not lost for good: it
   * stays retrievable through GET /apps/{id}/credentials, which is how the
   * credentials step fetches it on demand.)
   */
  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!justOpened) return;

    setError(null);
    setCredentials(null);
    setCreateForm({ name: '', description: '' });
    // Not reset before this change, and all four are template-scoped: opening
    // template B straight after template A showed A's versions, A's lenses and
    // A's "read N ago" line until the fresh loads landed.
    setLenses([]);
    setLensesError(null);
    setCreateNameError(null);
    setSelectedLensIds([]);
    setLensSelectionSeeded(false);
    setIsCreateLensOpen(false);
    setLastRefreshedAt(null);
    setPublishedTag(null);
    setSubmitAttempted(false);
    setEnvironmentErrors({ rows: {} });
    setEnvironmentTouched(false);
    lensBaselineRef.current = null;

    if (mode === 'new-version') {
      /*
       * A second version inherits the first one's declarations. Clearing them
       * the way setup does is data loss, not a clean slate: it silently narrows
       * a lidar or fused template to `camera`, and it asks the user to re-pick
       * every event type from memory — against a picker that shows UUID-keyed
       * names they have never seen written down anywhere else.
       *
       * `selectedVersion` first: that is the version actually running, so it is
       * what a new version is a successor to. `latestVersion` is the fallback
       * for a template whose versions were all registered inactive.
       */
      const prefill = template?.selectedVersion ?? template?.latestVersion ?? null;
      setSelectedTagIds(prefill?.supportedTags ?? []);
      setSelectedSensorTypes(prefill?.supportedSensorTypes ?? ['camera']);
      // Blank, never 'v1.0.0' — that tag is already published, and pre-filling
      // a value the flow then refuses is worse than pre-filling nothing.
      setImageTag('');
      // Carried over like the event types: a new version starts from what the
      // live one hands its container, secrets included (kept, never shown).
      const environmentSource = environmentSourceFor(template, 'new-version');
      const prefillEnvironment = environmentRowsFrom(environmentSource?.environment);
      setEnvironmentRows(prefillEnvironment);
      setEnvironmentFromVersionId(environmentSource?.id ?? null);
      submitBaselineRef.current = {
        imageTag: '',
        tagIds: prefill?.supportedTags ?? [],
        sensorTypes: prefill?.supportedSensorTypes ?? ['camera'],
        environment: prefillEnvironment,
      };
    } else {
      setSelectedTagIds([]);
      setSelectedSensorTypes(['camera']);
      setImageTag('v1.0.0');
      // Setup can resume on a template that already has versions, so it loads what
      // the page knows of them. An untouched list is not sent (environmentTouched),
      // so a version registered since the page loaded cannot be wiped.
      const environmentSource = environmentSourceFor(template, 'setup');
      const setupEnvironment = environmentRowsFrom(environmentSource?.environment);
      setEnvironmentRows(setupEnvironment);
      setEnvironmentFromVersionId(environmentSource?.id ?? null);
      submitBaselineRef.current = {
        imageTag: 'v1.0.0',
        tagIds: [],
        sensorTypes: ['camera'],
        environment: setupEnvironment,
      };
    }

    setAcks({ install: false, scaffold: false, build: false, push: false });
    setStepIndex(0);
    // A second version has no server-side resume point: everything the resume
    // logic can see is already complete. Start at the top of the short flow.
    setNeedsResume(mode === 'setup');
  }, [open, mode, template]);

  /**
   * One template's version history: a bounded read, so a resource (ruling 21).
   * Keyed on the template, so opening template B never shows A's versions; a
   * failure is flagged (versionsError) and never reads as "no versions".
   */
  const versionsResource = useResource<TemplateProcessorVersion[]>(
    () => apiService.getTemplateProcessorVersions(activeTemplate?.slug ?? '', organizationId, accessToken),
    {
      enabled: open && Boolean(activeTemplate && accessToken && organizationId),
      resetKey: activeTemplate ? `${organizationId}:${activeTemplate.id}` : null,
      deps: [open],
    },
  );
  const versions = versionsResource.data ?? NO_VERSIONS;
  const versionsError = versionsResource.status === 'error' ? versionsResource.error : null;
  /** Re-reads after a write this dialog made; resolves once it lands. */
  const loadVersions = versionsResource.refetch;
  /** The first read has answered (or there is no template to read for). */
  const versionsSettled = versionsResource.status !== 'loading';

  /**
   * Which lenses the template belongs to ON THE SERVER, as last read.
   *
   * The baseline the local selection starts from, and what tells the Summary
   * checklist whether routing was ever actually saved.
   */
  const savedLensIds = useMemo(
    () =>
      new Set(
        lenses
          // A lens created in the nested dialog may arrive without its template list.
          .filter((lens) => (lens.processorTemplates ?? []).some((ref) => ref.id === activeTemplate?.id))
          .map((lens) => lens.id),
      ),
    [lenses, activeTemplate],
  );
  const isAttachedToALens = activeTemplate != null && savedLensIds.size > 0;
  /** What the user has ticked. Nothing is written until Submit for Review. */
  const hasLensSelection = selectedLensIds.length > 0;


  /** True once the lens baseline has been read since opening. */
  const [contextLoaded, setContextLoaded] = useState(false);
  /** Both server truths the resume and seed logic derive from have answered. */
  const contextReady = contextLoaded && versionsSettled;

  /**
   * The organization's event types for the picker: an unbounded set, loaded in
   * batches with server search while the wizard is open (ruling 21).
   */
  const tagsList = useInfiniteList<Tag, 'name'>({
    ...batchFetcher<Tag, 'name'>(async ({ limit, offset, cursor, search, signal }) => {
      const data = await apiService.getTags(organizationId, accessToken, {
        limit,
        offset,
        cursor,
        search,
        sortBy: 'name',
        sortDirection: 'asc',
        signal,
      });
      return { items: data.tags, pagination: data.pagination };
    }),
    rowKey: (tag) => tag.id,
    enabled: open && Boolean(accessToken && organizationId),
    resetKey: organizationId,
    errorContext: 'Failed to load event types',
  });

  /*
   * A selected id outside the loaded batches — a `new-version` prefill carries
   * the previous version's `supportedTags`, under no batch limit — is fetched
   * by id and pinned first, so it stays visible and removable instead of being
   * silently re-submitted. An id the server no longer returns says so.
   */
  const loadedTagIds = useMemo(() => new Set(tagsList.items.map((tag) => tag.id)), [tagsList.items]);
  const missingTagKey = useMemo(
    () => selectedTagIds.filter((id) => !loadedTagIds.has(id)).sort().join(','),
    [selectedTagIds, loadedTagIds],
  );
  const selectedTagsResource = useResource(
    async (signal) => {
      const ids = missingTagKey.split(',').filter(Boolean).slice(0, 100);
      const data = await apiService.getTags(organizationId, accessToken, {
        limit: Math.max(1, ids.length),
        offset: 0,
        search: '',
        sortBy: 'name',
        sortDirection: 'asc',
        ids,
        signal,
      });
      return { ids, tags: data.tags };
    },
    {
      enabled: open && Boolean(missingTagKey && accessToken && organizationId) && tagsList.status !== 'initial',
      deps: [missingTagKey, open],
    },
  );
  const tagOptions = useMemo<PickableTag[]>(() => {
    const toOption = (tag: Tag): PickableTag => ({ id: tag.id, name: tag.name, slug: tag.slug });
    const loaded = tagsList.items.map(toOption);
    if (tagsList.search) return loaded;
    const fetched = selectedTagsResource.data;
    const pinned = selectedTagIds
      .filter((id) => !loadedTagIds.has(id))
      .flatMap((id): PickableTag[] => {
        const found = fetched?.tags.find((tag) => tag.id === id);
        if (found) return [toOption(found)];
        // Only once the by-id read answered without it: it really is gone.
        if (fetched?.ids.includes(id)) return [{ id, name: 'Event type no longer exists', slug: id }];
        return [];
      });
    return pinned.length > 0 ? [...pinned, ...loaded] : loaded;
  }, [tagsList.items, tagsList.search, selectedTagsResource.data, selectedTagIds, loadedTagIds]);

  /*
   * The lens checklist: batches with server search (ruling 21), fresh while the
   * wizard is open. `lenses` below is the routing truth the selection is seeded
   * from: only the lenses that hold THIS template on the server
   * (GET /lenses?templateId=…), never a whole-list read.
   */
  const lensesList = useInfiniteList<Lens, 'name'>({
    ...batchFetcher<Lens, 'name'>(async ({ limit, offset, cursor, search, signal }) => {
      const data = await apiService.getLensesBatch(organizationId, accessToken, {
        limit,
        offset,
        cursor,
        search: search || undefined,
        sortBy: 'name',
        sortDirection: 'asc',
        signal,
      });
      return { items: data.lenses, pagination: data.pagination };
    }),
    rowKey: (lens) => lens.id,
    enabled: open && Boolean(accessToken && organizationId),
    resetKey: organizationId,
    errorContext: 'Failed to load lenses',
  });
  /*
   * Ticked lenses outside the loaded batches are pinned first. The saved ones
   * come with the baseline; any other (ticked in a batch that a later search
   * or reload dropped) is fetched by id.
   */
  const knownLensIds = useMemo(
    () => new Set([...lensesList.items, ...lenses].map((lens) => lens.id)),
    [lensesList.items, lenses],
  );
  const missingLensKey = useMemo(
    () => selectedLensIds.filter((id) => !knownLensIds.has(id)).sort().join(','),
    [selectedLensIds, knownLensIds],
  );
  const selectedLensesResource = useResource<Lens[]>(
    async (signal) => {
      const ids = missingLensKey.split(',').filter(Boolean).slice(0, 100);
      const data = await apiService.getLensesBatch(organizationId, accessToken, {
        limit: Math.max(1, ids.length),
        offset: 0,
        ids,
        signal,
      });
      return data.lenses;
    },
    {
      enabled: open && Boolean(missingLensKey && accessToken && organizationId) && lensesList.status !== 'initial',
      deps: [missingLensKey, open],
    },
  );
  const lensRows = useMemo(() => {
    const loadedIds = new Set(lensesList.items.map((lens) => lens.id));
    if (lensesList.search) return lensesList.items;
    const pinned = new Map<string, Lens>();
    for (const lens of [...lenses, ...(selectedLensesResource.data ?? [])]) {
      if (selectedLensIds.includes(lens.id) && !loadedIds.has(lens.id)) pinned.set(lens.id, lens);
    }
    return [...pinned.values(), ...lensesList.items];
  }, [lensesList.items, lensesList.search, lenses, selectedLensesResource.data, selectedLensIds]);
  /** The lens requirement line shows only once there are lenses to pick from and none is picked. */
  const showLensRequirement =
    !isLensesLoading && lensesError == null && lensRows.length > 0 && !hasLensSelection;

  /**
   * The routing baseline: the lenses that hold this template on the server,
   * batch by batch over that bounded set. No template yet (setup, before
   * Create), no lenses.
   */
  const loadLenses = useCallback(async () => {
    if (!accessToken || !organizationId) return;
    if (!activeTemplate) {
      setLenses([]);
      setLensesError(null);
      return;
    }
    try {
      setIsLensesLoading(true);
      setLensesError(null);
      const found: Lens[] = [];
      let cursor: string | undefined;
      let offset = 0;
      // A lens can hold a template only once, so the set is bounded; the
      // guard only stops a server that never says it is done.
      for (let batch = 0; batch < 40; batch += 1) {
        const data = await apiService.getLensesBatch(organizationId, accessToken, {
          limit: 50,
          ...(cursor ? { cursor } : { offset }),
          templateId: activeTemplate.id,
          sortBy: 'name',
          sortDirection: 'asc',
        });
        found.push(...data.lenses);
        offset += data.lenses.length;
        if (!data.pagination?.hasMore || data.lenses.length === 0) break;
        cursor = data.pagination.nextCursor ?? undefined;
      }
      setLenses(found);
    } catch (err: unknown) {
      console.error('Failed to load lenses:', err);
      setLensesError(err);
    } finally {
      setIsLensesLoading(false);
    }
  }, [accessToken, organizationId, activeTemplate]);

  /**
   * Read both pieces of server-side truth, then let the resume effect place
   * the cursor. Deriving the step before these land would always compute
   * "step 1", which is the bug this replaces.
   */
  useEffect(() => {
    if (!open) {
      setContextLoaded(false);
      return;
    }
    let cancelled = false;
    const run = async () => {
      // Versions load through their resource as the dialog opens; this waits
      // for the lens baseline, and contextReady waits for both.
      await loadLenses();
      if (!cancelled) setContextLoaded(true);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [open, loadLenses]);

  /*
   * Seed the ticked boxes from the server ONCE per opening.
   *
   * Once, not on every `lenses` change: after this the selection is the user's,
   * and re-seeding would undo a box they just unticked the moment any reload
   * landed.
   */
  useEffect(() => {
    // Never seed from a failed read: that would tick nothing and call it done.
    if (!open || lensSelectionSeeded || !contextReady || lensesError != null) return;
    setSelectedLensIds([...savedLensIds]);
    lensBaselineRef.current = [...savedLensIds];
    setLensSelectionSeeded(true);
  }, [open, lensSelectionSeeded, contextReady, savedLensIds, lensesError]);

  /**
   * Completion derived from server state, never from where the user clicked.
   *
   * The flow crosses into a terminal and back, so the terminal can change
   * server state behind this dialog. Re-deriving on every render is what makes
   * closing and reopening cost nothing.
   */
  const completedIds = useMemo(() => {
    const done: string[] = [];
    if (activeTemplate) done.push('create');
    if (activeTemplate?.hasCredentials || credentials) done.push('credentials');
    for (const id of ACK_STEP_IDS) {
      if (acks[id]) done.push(id);
    }
    // A published version is proof the local steps happened — the image could
    // not exist otherwise. Without this the acks, which live only in component
    // state, reset on every open and would strand a returning user back on
    // "Install the SDK" forever.
    //
    // Setup only. In `new-version` mode this rule is true by definition on
    // arrival — the template has shipped — so applying it would mark build and
    // push done before the user has built anything, paint the whole rail green
    // and drop them on the read-only Verify screen. That is exactly the bug
    // this mode exists to fix.
    if (mode === 'setup' && versions.length > 0) done.push('install', 'scaffold', 'build', 'push');
    /*
     * "Event types are declared" means something different per mode.
     *
     * In setup it is a fact about the template: any version carrying tags
     * proves the step was done. In new-version that same fact was already true
     * when the dialog opened, so the only honest signal is this session's own
     * successful register.
     */
    const declared =
      mode === 'new-version'
        ? publishedTag !== null
        : versions.some((version) => (version.supportedTags?.length ?? 0) > 0);
    // Both halves, because the step submits both. A template that declared its
    // event types but belongs to no lens has nothing a reviewer could approve
    // into service — the version would go live and still match no subscription.
    if (declared && isAttachedToALens) done.push('submit');
    if (declared && isAttachedToALens && activeTemplate?.processorTemplateVersionId) {
      done.push('summary');
    }
    return Array.from(new Set(done));
  }, [activeTemplate, credentials, acks, versions, isAttachedToALens, mode, publishedTag]);

  /**
   * Land on the first thing still to do, not always on step 1.
   *
   * This is the whole payoff of deriving completion from server state: a user
   * who published from the terminal and came back gets put where they actually
   * are. Runs once per opening, after the first load settles.
   */
  useEffect(() => {
    // A failed read would place the cursor on a false picture; wait for Retry.
    if (!needsResume || !contextReady || versionsError != null || lensesError != null) return;
    const firstIncomplete = steps.findIndex((step) => !completedIds.includes(step.id));
    setStepIndex(firstIncomplete === -1 ? steps.length - 1 : firstIncomplete);
    setNeedsResume(false);
  }, [needsResume, contextReady, completedIds, steps, versionsError, lensesError]);

  // Never index blind: `stepIndex` is state that outlives a step list, so a
  // list shorter than the last index would otherwise read `undefined` and the
  // very next line would throw on `.id`.
  const currentStep = steps[stepIndex] ?? steps[steps.length - 1];
  const isTerminalStep = TERMINAL_STEP_IDS.has(currentStep.id);

  const orgQuery = organizationId || '<ORG_UUID>';

  /**
   * Advancing past a terminal step is the acknowledgement.
   *
   * These four steps happen in a shell we cannot observe, so someone has to
   * assert they are done. Asking for a checkbox *and* a Next click was two
   * gestures for one decision — pressing Next already says "I did that".
   *
   * Declared here rather than beside `goBack` because `handleRegisterVersion`
   * calls it: advancing off the Submit for Review step is a plain "next", and
   * the previous `setStepIndex(STEPS.findIndex(s => s.id === 'lens'))` returned
   * -1 for any list without a Lens step.
   */
  const goNext = () => {
    const current = steps[stepIndex];
    if (current && (ACK_STEP_IDS as readonly string[]).includes(current.id)) {
      setAcks((prev) => ({ ...prev, [current.id as AckStepId]: true }));
    }
    setError(null);
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  };

  const goBack = () => {
    setError(null);
    setStepIndex((i) => Math.max(i - 1, 0));
  };

  /**
   * The tag as it will be compared and published.
   *
   * Trimmed for the duplicate check only; the API's own format check (which
   * `isValidImageTag` mirrors) rejects whitespace outright, so a padded value
   * is still refused rather than silently cleaned up.
   */
  const trimmedImageTag = imageTag.trim();

  /**
   * Republishing a tag that already exists is blocked, not warned about.
   *
   * There is no unique index on the tag and no server-side check: POST
   * .../versions resolves whatever the tag points at in ECR *now* and inserts a
   * second row for it. Since API migration 0013 that row lands pending rather
   * than live, so a duplicate no longer silently changes what the template
   * runs — but it still produces two indistinguishable rows in the history and
   * a second review for the same tag. This check is the only guard there is.
   */
  const duplicateTag =
    trimmedImageTag !== '' && versions.some((version) => version.tag === trimmedImageTag);
  const duplicateTagMessage = !duplicateTag
    ? null
    : publishedTag !== null && publishedTag.trim() === trimmedImageTag
      ? // Reached by going Back after a successful publish. Still blocked, for
        // exactly the same reason — but "already exists" reads as a failure
        // when the user is looking at their own success.
        `You published "${trimmedImageTag}" a moment ago. Publishing it again would add a second version row for the same image. Push a new tag to ship another version.`
      : `Version "${trimmedImageTag}" already exists for this template. Publishing it again would add a second version pointing at whatever that tag resolves to now, and send it for review as a separate submission. Push a new tag instead.`;

  /*
   * Dirty = step input not yet committed to the server. The create step's
   * name and description until the template exists; the Submit step's tag,
   * event types, sensor types, lenses and environment variables once they differ from what this
   * opening seeded, until exactly that tag has been submitted. Acknowledgement
   * ticks are never dirty.
   */
  const sameMembers = (a: string[], b: string[]) => a.length === b.length && a.every((item) => b.includes(item));
  const createStepDirty = !activeTemplate && Boolean(createForm.name.trim() || createForm.description.trim());
  const submitBaseline = submitBaselineRef.current;
  const lensBaseline = lensBaselineRef.current;
  const submitStepEdited =
    submitBaseline !== null &&
    (trimmedImageTag !== submitBaseline.imageTag.trim() ||
      !sameMembers(selectedTagIds, submitBaseline.tagIds) ||
      !sameMembers(selectedSensorTypes, submitBaseline.sensorTypes) ||
      (lensBaseline !== null && !sameMembers(selectedLensIds, lensBaseline)) ||
      !sameEnvironment(environmentRows, submitBaseline.environment));
  const submittedAsIs = publishedTag !== null && publishedTag.trim() === trimmedImageTag;
  const wizardDirty = createStepDirty || (Boolean(activeTemplate) && submitStepEdited && !submittedAsIs);

  // X, Escape, outside click and Done all route through one guard. On a
  // terminal step Escape and outside click are blocked first (below), so only
  // the X reaches it there.
  const guard = useDiscardGuard({
    isDirty: wizardDirty,
    isBusy: busy,
    onClose: () => onOpenChange(false),
    recordNoun: template ? `the new version of "${template.name}"` : 'the new template',
  });

  /** Step 1 — create the template. */
  const handleCreate = async () => {
    if (!createForm.name.trim()) return;
    try {
      setBusy(true);
      setError(null);
      setCreateNameError(null);
      const created = await apiService.createTemplateProcessor(
        { name: createForm.name.trim(), description: createForm.description.trim() || undefined },
        organizationId,
        accessToken,
      );
      setCredentials(created.credentials);
      onTemplateChanged(created);
      toast.success('Template created');
      setStepIndex(1);
    } catch (err: unknown) {
      console.error('Failed to create template:', err);
      // The slug is derived from the name, so a taken slug is the name's problem.
      const failure = classifyError(err);
      if (failure.kind === 'conflict' && (failure.field === 'slug' || failure.field === 'name')) {
        setCreateNameError('A template with this name already exists in this organization. Choose a different name.');
      } else {
        setError(humanizeError(err, "Couldn't create the template"));
      }
    } finally {
      setBusy(false);
    }
  };

  /** Step 2 — read the publisher credential back. */
  const handleReadCredentials = async () => {
    if (!activeTemplate?.publisherAppClientId) return;
    try {
      setBusy(true);
      setError(null);
      const found = await apiService.getAppCredentials(activeTemplate.publisherAppClientId, accessToken);
      setCredentials({ clientId: found.appClientId, clientSecret: found.clientSecret });
    } catch (err: unknown) {
      console.error('Failed to read publisher credentials:', err);
      setError(humanizeError(err, "Couldn't read the publisher credential"));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Submit for Review — register the pushed tag with its event types and its
   * lens routing, sending it to an administrator.
   *
   * `isActive: true` below is now INERT: since API migration 0013 registering a
   * version submits it, and only an administrator's approval makes it live. It
   * is left in place deliberately rather than deleted — the field is still part
   * of the request shape, and removing it here would suggest the client had a
   * say in the outcome when the point is that it does not.
   */
  const handleRegisterVersion = async () => {
    if (!activeTemplate) return;
    setSubmitAttempted(true);
    // Every environment problem shows under its own field, whatever else fails first.
    const environmentProblems = validateEnvironmentRows(environmentRows, { kubernetesSecretNames });
    setEnvironmentErrors(environmentProblems);
    if (!isValidImageTag(imageTag)) {
      setError('Image tags can use letters, numbers, dots, underscores and hyphens — up to 64 characters.');
      return;
    }
    if (selectedTagIds.length === 0) {
      setError('Select at least one event type, or subscribers will never reach this processor.');
      return;
    }
    // Checked here as well as in the button's `disabled`, because a lens can be
    // unticked by the checkbox directly above the button — and because the
    // reviewer is being asked to approve routing that would otherwise be empty.
    if (!hasLensSelection) {
      setError('Select at least one lens before submitting — a reviewer approves the routing along with the image.');
      return;
    }
    if (duplicateTagMessage) {
      setError(duplicateTagMessage);
      return;
    }
    if (hasEnvironmentErrors(environmentProblems)) {
      setError(null);
      return;
    }

    try {
      setBusy(true);
      setError(null);
      /*
       * ONE request. The event types, the sensor types and the lens routing
       * all land in a single server-side transaction, so a submission cannot
       * half-apply: no version without its routing, no routing without its
       * version.
       */
      await apiService.createTemplateProcessorVersion(
        activeTemplate.slug,
        {
          tag: imageTag,
          supportedTags: selectedTagIds,
          supportedSensorTypes: selectedSensorTypes,
          lensIds: selectedLensIds,
          ...(environmentFromVersionId || environmentTouched
            ? { environment: toEnvironmentPayload(environmentRows) }
            : {}),
          ...(environmentFromVersionId ? { environmentFromVersionId } : {}),
          isActive: true,
        },
        organizationId,
        accessToken,
      );
      // Both reloads read state this request just changed, so they follow it
      // rather than racing it.
      await Promise.all([loadVersions(), loadLenses()]);
      const refreshed = await apiService.getTemplateProcessors(organizationId, accessToken);
      const mine = refreshed.find((item) => item.id === activeTemplate.id);
      if (mine) onTemplateChanged(mine);
      toast.success('Version submitted for review', {
        description: 'An administrator has to approve it before it runs against any sensors.',
      });
      // Recorded before advancing: it is what marks the Submit for Review step
      // done in new-version mode, where no server-side fact can distinguish
      // this version from the ones that were already there.
      setPublishedTag(imageTag);
      // A plain "next" — Submit for Review → Summary in both modes. 'submit' is
      // not an ack step, so goNext's ack write is a no-op.
      goNext();
    } catch (err: unknown) {
      console.error('Failed to register version:', err);
      // Two refusals this step can explain better than the generic sentence:
      // a tag with no image behind it, and an environment the API will not
      // store (a backstop — the rows were checked before submitting). Branch on
      // the API's code, never the status number: every other 400 is generic.
      const { code } = classifyError(err);
      // A refused variable goes under its own field, like every other field
      // error on this step; the banner is only for a refusal that names none.
      const refusal = environmentRefusal(err);
      const refusedRow = refusal ? environmentRows.find((row) => row.name === refusal.name) : undefined;
      if (refusal && refusedRow) {
        setEnvironmentErrors({
          rows: {
            [refusedRow.rowId]: {
              [refusal.field]:
                refusal.field === 'value'
                  ? 'This value was refused. Enter it again.'
                  : 'This name was refused. Choose another one.',
            },
          },
        });
        setError(null);
        return;
      }
      setError(
        code === 'IMAGE_TAG_NOT_FOUND'
          ? `We couldn't find the tag "${imageTag}" in your registry. Run the push step first, then try again.`
          : code === 'INVALID_ENVIRONMENT'
            ? 'The environment variables were refused. Check each name and value, then submit again.'
            : humanizeError(err, "Couldn't publish the version"),
      );
    } finally {
      setBusy(false);
    }
  };

  /**
   * Tick a lens. No request — the submission carries the whole selection.
   */
  const toggleLens = (lensId: string) => {
    setError(null);
    setSelectedLensIds((prev) =>
      prev.includes(lensId) ? prev.filter((id) => id !== lensId) : [...prev, lensId],
    );
  };

  /**
   * Summary — re-read every source the checklist derives from.
   *
   * The server-derived rows (push, and the submission built on event types plus
   * lens) only ever change through this dialog within a single pass, and it
   * already reloads after each of its own writes — so a manual refresh button
   * would sit there fetching identical data and changing nothing, which is
   * worse than no button.
   *
   * What it cannot see is a change made elsewhere: CI publishing a version
   * while the user was in their terminal, or a teammate attaching the lens. So
   * this runs on arrival at the step instead of on a click.
   */
  const refreshStatus = useCallback(async () => {
    if (!activeTemplate) return;
    try {
      setIsRefreshing(true);
      await Promise.all([loadVersions(), loadLenses()]);
      const all = await apiService.getTemplateProcessors(organizationId, accessToken);
      const mine = all.find((item) => item.id === activeTemplate.id);
      if (mine) onTemplateChanged(mine);
      setLastRefreshedAt(new Date().toISOString());
    } catch (err: unknown) {
      // Deliberately not surfaced: the rows still show the last known state,
      // and an error banner on a read the user never asked for is noise.
      console.error('Failed to refresh template status:', err);
    } finally {
      setIsRefreshing(false);
    }
  }, [activeTemplate, organizationId, accessToken, loadVersions, loadLenses, onTemplateChanged]);

  useEffect(() => {
    if (!open || !contextReady) return;
    if (steps[stepIndex]?.id !== 'summary') return;
    void refreshStatus();
    // Re-reads on each arrival at the step, not on every render within it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contextReady, stepIndex]);

  const renderStep = () => {
    switch (currentStep.id) {
      case 'create':
        if (activeTemplate) {
          return (
            <div className="space-y-4">
              <Alert variant="success" announce>
                <AlertTitle>Template created</AlertTitle>
                <AlertDescription>
                  <span className="font-mono">{activeTemplate.slug}</span> is ready. Continue to its
                  publisher credential.
                </AlertDescription>
              </Alert>
              <p className="text-sm text-muted-foreground">
                The slug is permanent — it is this template&apos;s registry path and the name of its
                publisher credential. It cannot be renamed or released.
              </p>
            </div>
          );
        }
        return (
          <div className="space-y-4">
            <Field label="Name" required error={createNameError}>
              {(control) => (
                <Input
                  {...control}
                  value={createForm.name}
                  onChange={(event) => setCreateForm((prev) => ({ ...prev, name: event.target.value }))}
                  placeholder="Vehicle Counter Pro"
                  autoComplete="off"
                  required
                />
              )}
            </Field>

            {createForm.name.trim() ? (
              // A derived value inside a create task is a FieldValue, never an
              // input-styled well (DESIGN_SYSTEM.md §5 Record dialogs).
              <FieldValue
                orientation="vertical"
                label="Slug"
                mono
                help="Permanent. This becomes your registry path and your publisher credential name."
              >
                {previewSlug(createForm.name)}
              </FieldValue>
            ) : null}

            {/* The same control as the workbench's Description: a description
                is always a Textarea, and the missing required marker already
                says it is optional. */}
            <Field label="Description">
              {(control) => (
                <Textarea
                  {...control}
                  rows={2}
                  value={createForm.description}
                  onChange={(event) =>
                    setCreateForm((prev) => ({ ...prev, description: event.target.value }))
                  }
                />
              )}
            </Field>
          </div>
        );

      case 'credentials':
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              These were created with the template. The secret is permanent and cannot be rotated —
              store it in a secrets manager, not in a repository.
            </p>

            {credentials ? (
              <div className="space-y-3">
                <CredentialField label="Client ID" value={credentials.clientId} />
                <CredentialField label="Client secret" value={credentials.clientSecret} secret revealable={false} />
              </div>
            ) : mayReadCredentials && activeTemplate?.publisherAppClientId ? (
              <Button type="button" variant="outline" onClick={() => void handleReadCredentials()} loading={busy}>
                Show publisher credential
              </Button>
            ) : (
              <Alert variant="warning">
                <AlertTitle>You need another permission for this step</AlertTitle>
                <AlertDescription>
                  Reading a publisher credential requires the &quot;{permissionLabel('app_clients:write')}&quot; and
                  &quot;{permissionLabel('processor_template_versions:write')}&quot; permissions. Ask an organization
                  admin to grant them in Roles, then reopen this flow.
                </AlertDescription>
              </Alert>
            )}
          </div>
        );

      case 'install':
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              The SDK needs Python 3.11 or newer. A virtual environment keeps it off your system Python.
            </p>
            <CommandBlock
              label="your machine"
              copyLabel="install commands"
              command={[
                'python3.11 -m venv .venv && source .venv/bin/activate',
                '',
                `pip install "dory-processor-sdk[production]==${SDK_VERSION}"`,
                'pip install requests',
                '',
                `curl -O ${CLI_DOWNLOAD_URL}`,
              ].join('\n')}
            />
            <Alert variant="info">
              <AlertDescription>
                Quote the brackets — zsh treats <span className="font-mono">[production]</span> as a glob
                pattern otherwise.
              </AlertDescription>
            </Alert>
          </div>
        );

      case 'scaffold':
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This generates a complete project: processor source, a Dockerfile pinned to{' '}
              <span className="font-mono">linux/amd64</span>, an SDK config, and an optional CI workflow.
            </p>
            <CommandBlock
              label="your machine"
              copyLabel="scaffold command"
              command={[
                'python3 dory_util.py scaffold \\',
                `  --name "${activeTemplate?.name ?? 'My Template'}" \\`,
                `  --slug ${slug} \\`,
                `  --path ./${slug} \\`,
                `  --sdk-version ${SDK_VERSION} \\`,
                '  --ci github',
              ].join('\n')}
            />
            <p className="text-sm text-muted-foreground">
              Then edit <span className="font-mono">main.py</span> — the generated processor already
              starts, keeps state across restarts, and shuts down gracefully.
            </p>
          </div>
        );

      case 'build':
        return (
          <div className="space-y-4">
            <CommandBlock
              label="your machine"
              copyLabel="build commands"
              // Deliberately no `cd`. The next step runs dory_util.py, which
              // was downloaded here in step 3 — stepping into the project
              // directory to build would leave it one level up, and the push
              // command would fail with "No such file or directory". Docker
              // takes the build context as an argument, so staying put costs
              // nothing and keeps the whole flow in one working directory.
              command={[
                'docker buildx build --platform linux/amd64 \\',
                `  -t ${slug}:latest --load ./${slug}`,
              ].join('\n')}
            />
            <Alert variant="warning">
              <AlertTitle>Build for linux/amd64, always</AlertTitle>
              <AlertDescription>
                Processors run on amd64 hosts. Nothing checks architecture when you push, so an arm64
                image built on an Apple Silicon Mac uploads and registers cleanly, then fails at
                startup with <span className="font-mono">exec format error</span>.
              </AlertDescription>
            </Alert>
            <p className="text-sm text-muted-foreground">
              Want to try it first? Run{' '}
              <span className="font-mono">{`(cd ${slug} && DORY_HEALTH_PORT=8080 python main.py)`}</span>{' '}
              and open <span className="font-mono">localhost:8080/health</span>. The parentheses keep
              the directory change inside a subshell, so your next command still finds{' '}
              <span className="font-mono">dory_util.py</span>. A &ldquo;RabbitMQ publisher not
              configured&rdquo; warning is expected locally.
            </p>
          </div>
        );

      case 'push': {
        /*
         * The command is built twice: once masked for the screen, once real for
         * the clipboard.
         *
         * The secret has to reach the terminal — telling the user to go back a
         * step and paste it in by hand is the friction this whole flow exists to
         * remove. But the Credentials step deliberately shows it masked with no
         * reveal, so rendering it verbatim here would put it on screen through
         * the back door, in a `<pre>` that is on screen for as long as the push
         * takes. CommandBlock's `copyText` is exactly the seam for that.
         *
         * The client ID is not a secret — it is on the Credentials step and on the
         * template itself — so it is interpolated into both.
         */
        const clientIdValue =
          credentials?.clientId ??
          activeTemplate?.publisherAppClientId ??
          '<Client ID from the Get publisher credential step>';
        const clientSecretValue = credentials?.clientSecret ?? null;
        /*
         * No `#` comment lines, in either copy.
         *
         * zsh does not enable INTERACTIVE_COMMENTS by default, so a pasted
         * block containing them dies on `zsh: bad pattern: #` before reaching
         * the command that mattered. Everything those comments said is in the
         * prose around the block, where it is also readable before the paste.
         */
        const pushCommand = (secret: string) =>
          [
            'unset DORY_ACCESS_TOKEN',
            `export DORY_CLIENT_ID="${clientIdValue}"`,
            `export DORY_CLIENT_SECRET="${secret}"`,
            '',
            'python3 dory_util.py push-image \\',
            `  --tag ${trimmedImageTag || '<image tag>'} \\`,
            `  --local-image ${slug}:latest`,
          ].join('\n');
        const shownCommand = pushCommand(
          clientSecretValue ? SECRET_MASK : '<Client secret from the Get publisher credential step>',
        );
        const copyCommand = clientSecretValue
          ? pushCommand(clientSecretValue)
          : shownCommand;

        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload the image to this template&apos;s own registry. You will declare its event types
              and its lens in the next step — that is what submits the version for review.
            </p>

            {/*
              The tag is chosen HERE, not two steps later: the command below
              carries it, so asking for it afterwards meant the copied command
              said `v1.0.0` while the version being published said something
              else. The value is still what the Submit for review step registers.
            */}
            <Field
              label="Image tag"
              required
              help="The tag you are about to push. It becomes this version's identity."
              error={duplicateTagMessage}
            >
              {(control) => (
                <Input
                  {...control}
                  mono
                  value={imageTag}
                  onChange={(event) => setImageTag(event.target.value)}
                  placeholder="v1.1.0"
                  autoComplete="off"
                  required
                />
              )}
            </Field>

            <CommandBlock
              label="your machine"
              copyLabel="push commands"
              command={shownCommand}
              copyText={copyCommand}
            />

            {clientSecretValue ? (
              <p className="text-sm text-muted-foreground">
                The secret is masked on screen. <strong className="font-medium">Copy</strong> puts the
                real value on your clipboard, so the block pastes into a terminal and runs as-is.
              </p>
            ) : (
              <Alert variant="info">
                <AlertDescription>
                  Go back to <strong className="font-medium">Get publisher credential</strong> and show
                  this template&apos;s credential — the copied command will then carry the secret for
                  you instead of leaving a placeholder in it.
                </AlertDescription>
              </Alert>
            )}

            <p className="text-sm text-muted-foreground">
              <span className="font-mono">unset DORY_ACCESS_TOKEN</span> comes first because a user
              token outranks these credentials. Don&apos;t set{' '}
              <span className="font-mono">DORY_SCOPE</span> — left alone, the token carries every
              scope the credential holds, and asking for one it lacks fails the whole request.
            </p>
          </div>
        );
      }

      case 'submit': {
        return (
          <div className="space-y-4">
            <Alert variant="info">
              <AlertTitle>This is what the reviewer sees</AlertTitle>
              <AlertDescription>
                Event types, lens and environment variables are declared here, before the version
                goes for review, so an administrator approves the whole configuration rather than the
                image alone. Event types and lens are also what makes the processor reachable: a
                version with no event types can never match a subscription, and a template in no lens
                is invisible to every one.
              </AlertDescription>
            </Alert>

            {/*
              Read-only here: the tag is entered on the Upload step, because the
              push command has to carry it. Showing it anyway is what tells the
              user which image this button is about to submit.
            */}
            <FieldValue orientation="vertical" label="Image tag" mono={Boolean(trimmedImageTag)}>
              {trimmedImageTag || 'Not set — go back to Upload version container'}
            </FieldValue>

            {duplicateTagMessage ? (
              <Alert variant="warning">
                <AlertTitle>That tag is already published</AlertTitle>
                <AlertDescription>{duplicateTagMessage}</AlertDescription>
              </Alert>
            ) : null}

            <TagPicker
              legend="Event types"
              help="What this processor emits."
              tags={tagOptions}
              selectedIds={selectedTagIds}
              isLoading={tagsList.status === 'initial' && tagsList.error == null}
              disabled={busy}
              searchInput={tagsList.searchInput}
              onSearchChange={tagsList.setSearchInput}
              search={tagsList.search}
              listStatus={tagsList.status}
              hasMore={tagsList.hasMore}
              onLoadMore={tagsList.loadMore}
              onRetryBatch={tagsList.retryBatch}
              onReset={tagsList.reset}
              totalCount={tagsList.totalCount}
              loadError={tagsList.error != null && tagsList.status !== 'appendError' ? tagsList.error : undefined}
              onRetry={tagsList.reset}
              isRetrying={tagsList.error != null && (tagsList.status === 'initial' || tagsList.status === 'refreshing')}
              onToggle={(tagId) =>
                setSelectedTagIds((prev) =>
                  prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId],
                )
              }
              emptyMessage="No event types exist for this organization yet. Create one on the Event Types page first."
            />

            {/* A short fixed enum: an inline group, no count or filter. */}
            <FieldGroup legend="Sensor types" orientation="inline">
                {SENSOR_TYPES.map((sensorType) => (
                  <div key={sensorType} className="flex items-center gap-2">
                    <Checkbox
                      id={`sensor-${sensorType}`}
                      aria-describedby={`sensor-${sensorType}-key`}
                      checked={selectedSensorTypes.includes(sensorType)}
                      disabled={busy}
                      onCheckedChange={() =>
                        setSelectedSensorTypes((prev) =>
                          prev.includes(sensorType)
                            ? prev.filter((item) => item !== sensorType)
                            : [...prev, sensorType],
                        )
                      }
                    />
                    {/* The label people read, then the key the container receives. */}
                    <Label htmlFor={`sensor-${sensorType}`} className="cursor-pointer font-normal">
                      {SENSOR_TYPE_LABELS[sensorType]}
                    </Label>
                    <span id={`sensor-${sensorType}-key`} className="font-mono text-xs text-muted-foreground">
                      {sensorType}
                    </span>
                  </div>
                ))}
            </FieldGroup>

            {!mayWriteLenses ? (
              <Alert variant="warning">
                <AlertTitle>You can submit, but not re-route</AlertTitle>
                <AlertDescription>
                  Changing which lenses this template belongs to requires the &quot;
                  {permissionLabel('lenses:write')}&quot; and &quot;
                  {permissionLabel('processor_template_versions:write')}&quot; permissions. You can
                  still submit a version while the selection below is left as it is — ask an
                  organization admin for the permission to change it.
                </AlertDescription>
              </Alert>
            ) : null}

            {/*
              Selection is held by id, so neither control here can move it: a
              lens created from the + button is pinned first and ticked
              (prependItem), and Refresh re-reads the list for a lens made
              elsewhere — ticked lenses outside the loaded batches stay pinned.
              Help until a submit is tried, an error after (DESIGN_SYSTEM §5
              Validation): nothing is wrong before the user has asked for anything.
            */}
            <CheckboxList
              legend="Lenses"
              required
              idPrefix="lens"
              itemNoun="lenses"
              searchPlaceholder="Search lenses…"
              description="A lens is how subscribers find your processor. Nothing is saved until you submit — the routing goes to the reviewer with the version, in one step."
              actions={
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => {
                      void loadLenses();
                      lensesList.reset();
                    }}
                    disabled={busy}
                    loading={isLensesLoading || lensesList.status === 'initial' || lensesList.status === 'refreshing'}
                    aria-label="Refresh lenses"
                  >
                    <RefreshCw aria-hidden="true" />
                  </Button>
                  {/* Absent without lenses:write (rung 1 of the unavailable-actions
                      ladder): the warning above already says what they can do. */}
                  {mayWriteLenses ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      onClick={() => setIsCreateLensOpen(true)}
                      disabled={busy}
                      aria-label="Create a new lens"
                    >
                      <Plus aria-hidden="true" />
                    </Button>
                  ) : null}
                </>
              }
              items={lensRows.map((lens) => ({
                id: lens.id,
                label: lens.name,
                meta: lens.alias,
                adornment: lensesList.marks.get(lens.id) === 'new' ? <StatusBadge status="new" /> : undefined,
              }))}
              selectedIds={new Set(selectedLensIds)}
              onToggle={toggleLens}
              disabled={busy || !mayWriteLenses}
              {...checkboxListProps(lensesList)}
              isLoading={
                (isLensesLoading || lensesList.status === 'initial') && lensesError == null && !hasListError(lensesList)
              }
              loadError={lensesError ?? (hasListError(lensesList) ? lensesList.error : undefined)}
              loadErrorTitle="Couldn't load lenses"
              onRetry={() => {
                if (lensesError != null) void loadLenses();
                if (hasListError(lensesList)) lensesList.reset();
              }}
              help={showLensRequirement && !submitAttempted ? LENS_REQUIREMENT : undefined}
              error={showLensRequirement && submitAttempted ? LENS_REQUIREMENT : null}
              emptyMessage={
                <Alert variant="warning">
                  <AlertTitle>No lenses to attach to</AlertTitle>
                  <AlertDescription>
                    This organization has no lenses yet, and a version cannot be submitted without
                    one.{' '}
                    {mayWriteLenses
                      ? 'Use the + button above to create one.'
                      : `Ask someone with the "${permissionLabel('lenses:write')}" permission to create one.`}
                  </AlertDescription>
                </Alert>
              }
            />

            <EnvironmentVariablesField
              rows={environmentRows}
              errors={environmentErrors}
              disabled={busy}
              kubernetesSecretNames={kubernetesSecretNames}
              onChange={(rows, change) => {
                setEnvironmentRows(rows);
                setEnvironmentTouched(true);
                setEnvironmentErrors((prev) =>
                  reconcileEnvironmentErrors(prev, rows, change, { kubernetesSecretNames }),
                );
              }}
            />
          </div>
        );
      }

      case 'summary': {
        const rows = steps.filter((step) => step.id !== 'summary').map((step) => ({
          ...step,
          done: completedIds.includes(step.id),
        }));
        // The rail can jump here from anywhere, so this step never claims
        // success it cannot back up: the first unfinished step comes first.
        const firstIncomplete = rows.find((row) => !row.done);
        // Keyed on a pending version, not on 'summary' being incomplete: in
        // new-version mode the template's live version is usually the PREVIOUS
        // one, so 'summary' is done while the new submission still waits.
        const awaitingReview = versions.some((version) => version.reviewStatus === 'pending');
        return (
          <div className="space-y-4">
            <div className="space-y-1 rounded-md border p-3">
              {rows.map((row) => (
                <div key={row.id} className="flex items-center justify-between gap-3 py-0.5 text-sm">
                  <span className={row.done ? 'text-foreground' : 'text-muted-foreground'}>{row.label}</span>
                  <span
                    className={
                      row.done ? 'text-xs text-success-emphasis' : 'text-xs text-muted-foreground'
                    }
                  >
                    {row.done ? 'Done' : 'Not yet'}
                  </span>
                </div>
              ))}
            </div>

            {firstIncomplete ? (
              <Alert variant="warning">
                <AlertTitle>{`${firstIncomplete.label} isn't done yet`}</AlertTitle>
                <AlertDescription>
                  <p>
                    {firstIncomplete.id === 'submit'
                      ? 'Nothing has been sent for review, so this version will not run.'
                      : 'Finish this step before the version can be reviewed.'}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => setStepIndex(steps.findIndex((step) => step.id === firstIncomplete.id))}
                  >
                    {`Go to ${firstIncomplete.label}`}
                  </Button>
                </AlertDescription>
              </Alert>
            ) : awaitingReview ? (
              <Alert variant="info">
                <AlertTitle>Waiting for review</AlertTitle>
                <AlertDescription>
                  An administrator has to approve this version before it runs.
                </AlertDescription>
              </Alert>
            ) : (
              <Alert variant="info">
                <AlertTitle>Configuration verified — not runtime</AlertTitle>
                <AlertDescription>
                  This confirms your template is set up correctly. It cannot yet confirm the container is
                  running: per-processor runtime status is not exposed to organizations today.
                </AlertDescription>
              </Alert>
            )}

            <p className="text-xs text-muted-foreground" aria-live="polite">
              {isRefreshing
                ? 'Checking…'
                : lastRefreshedAt
                  ? `Read ${formatRelativeTime(lastRefreshedAt)}`
                  : null}
            </p>
          </div>
        );
      }

      default:
        return null;
    }
  };

  /**
   * Only what makes Submit impossible to attempt: a request in flight, no
   * template, or a lens list that failed to load. Every input problem is left
   * to handleRegisterVersion, which names it (DESIGN_SYSTEM §5 Validation: a
   * multi-field form disables submit only while pending).
   */
  const submitBlocked = busy || !activeTemplate || lensesError != null;

  const primaryAction = () => {
    // A step that commits to the server puts that commit in this slot. Next
    // replaces Submit for review only once the step is complete and nothing
    // new waits to be sent (e.g. Back after a successful submission).
    if (
      currentStep.id === 'submit' &&
      (!completedIds.includes('submit') || (trimmedImageTag !== '' && !duplicateTag))
    ) {
      return (
        <Button type="button" onClick={() => void handleRegisterVersion()} disabled={submitBlocked} loading={busy}>
          Submit for review
        </Button>
      );
    }
    if (currentStep.id === 'create' && !activeTemplate) {
      return (
        <Button type="button" onClick={() => void handleCreate()} disabled={!createForm.name.trim()} loading={busy}>
          Create template
        </Button>
      );
    }
    if (stepIndex === steps.length - 1) {
      return (
        <Button type="button" onClick={guard.requestClose}>
          Done
        </Button>
      );
    }
    return (
      <Button type="button" onClick={goNext} disabled={busy}>
        Next
      </Button>
    );
  };

  return (
    // `pending={busy}` locks the steps that send requests (Create template,
    // Submit for review), whose results show inline; the terminal-step guards
    // below still run first and are never overridden.
    <Dialog open={open} pending={busy} onOpenChange={guard.onOpenChange}>
      <DialogContent
        /*
         * Fixed frame, not content-sized. The steps swing between a two-line
         * form and a stack of command blocks, and a dialog that resizes under
         * the pointer moves the Back/Next buttons between every step. 640px
         * clears the tallest step — now Submit for Review, which stacks the tag,
         * two pickers and the lens list — and the 85vh cap keeps it whole on a
         * short laptop screen.
         */
        size="xl"
        frame
        className="h-[640px]"
        // A terminal step means the user is mid-way through work in another
        // window. Escape and outside-click are the same gesture in Radix, and
        // losing the dialog to a stray keypress there is a real cost — so only
        // the explicit close button exits while those steps are showing.
        onEscapeKeyDown={(event) => {
          if (isTerminalStep) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (isTerminalStep) event.preventDefault();
        }}
      >
        {/*
          15rem, not 13.5: the rail labels are full phrases ("Upload Version
          Container"), and at 13.5rem every one of them wrapped to two lines,
          which pushed the eight rows past the aside's height.
        */}
        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden sm:grid-cols-[15rem_1fr]">
          {/* Left rail. Hidden below sm, where a horizontal strip fits better. */}
          <aside className="hidden min-h-0 overflow-y-auto border-r bg-muted/30 p-4 sm:block">
            <StepIndicator
              orientation="vertical"
              steps={steps}
              currentIndex={stepIndex}
              completedIds={completedIds}
              onSelect={activeTemplate ? setStepIndex : undefined}
            />
          </aside>

          <div className="flex min-h-0 flex-col">
            <DialogHeader className="shrink-0 border-b px-5 py-4 pr-12 text-left">
              {/*
                The title names the flow, because the two share a dialog. Setup
                keeps "Publish <name>" the moment step 1 mints the template —
                the continuity the create path is asserted on.
              */}
              <DialogTitle>
                {!activeTemplate
                  ? 'Create a processor template'
                  : mode === 'new-version'
                    ? `New version of ${activeTemplate.name}`
                    : `Publish ${activeTemplate.name}`}
              </DialogTitle>
              <DialogDescription>
                Step {stepIndex + 1} of {steps.length} — {currentStep.label}
              </DialogDescription>
            </DialogHeader>

            <div className="shrink-0 border-b px-5 py-3 sm:hidden">
              <StepIndicator
                steps={steps}
                currentIndex={stepIndex}
                completedIds={completedIds}
                onSelect={activeTemplate ? setStepIndex : undefined}
              />
            </div>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
              {versionsError != null && (currentStep.id === 'submit' || currentStep.id === 'summary') ? (
                <ErrorState
                  compact
                  title="Couldn't load this template's versions"
                  error={versionsError}
                  onRetry={() => void loadVersions()}
                />
              ) : null}

              {renderStep()}

            </div>

            {/* The step's failure, pinned above the footer and outside the scroll body. */}
            {error ? (
              <div className="shrink-0 px-5 pb-3">
                <FormError message={error} />
              </div>
            ) : null}

            <DialogFooter className="shrink-0 flex-col gap-2 border-t px-5 py-3 sm:flex-row sm:items-center sm:justify-between sm:space-x-0">
              <a
                href={GUIDE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                Full guide
                <ExternalLink aria-hidden="true" className="h-3 w-3" />
              </a>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={goBack} disabled={stepIndex === 0 || busy}>
                  Back
                </Button>
                {primaryAction()}
              </div>
            </DialogFooter>
          </div>
        </div>
      </DialogContent>
      {/*
        Rendered inside the wizard's Dialog so it stacks above it. It creates
        the lens and nothing else — the wizard pins it first and ticks it, so
        the attach still happens exactly once, at submit, with everything else.
      */}
      <CreateLensDialog
        open={isCreateLensOpen}
        onOpenChange={setIsCreateLensOpen}
        organizationId={organizationId}
        accessToken={accessToken}
        canMakePublic={mayMakeLensPublic}
        onCreated={(lens) => {
          // The created lens comes back: pin it first and tick it, with no
          // reload. CreateLensDialog's own toast is the one acknowledgement.
          lensesList.prependItem(lens, { mark: 'new' });
          setSelectedLensIds((prev) => (prev.includes(lens.id) ? prev : [...prev, lens.id]));
        }}
      />
      {guard.guardDialog}
    </Dialog>
  );
};
